<script lang="ts">
	import Layout from "../components/Layout.svelte";
	import { getTranslate } from "../utils/getTranslate.js";
	import translations from "./RnD.translations.json";
	import HeroBanner from "../components/HeroBanner.svelte";
	import CaseStudies from "../components/CaseStudies.svelte";
	import type { RnDProps } from "./rndProps";
	import StaticPicture from "../components/Picture/StaticPicture.svelte";
	import RNDImageSm from "../assets/images/rnd/rnd-idea-sm-text.svg";
	import RNDImageLg from "../assets/images/rnd/rnd-idea-lg.png?webp&w=1759&imagetools";
	import RNDImageSmEn from "../assets/images/rnd/rnd-idea-sm-en.svg";
	import RNDImageLgEn from "../assets/images/rnd/rnd-idea-lg-en.png?webp&w=1759&imagetools";
	import RNDImage from "../assets/images/RND.jpeg?webp&w=1920&imagetools";
	import RND1 from "../assets/images/rnd/RND1.png?webp&w=945&imagetools";
	import RND2 from "../assets/images/rnd/RND2.jpg?webp&w=945&imagetools";
	import RND3 from "../assets/images/rnd/RND3.png?webp&w=945&imagetools";
	import researchHeader from "../assets/videos/brano-research-development-video.mp4";
	import researchPoster from "../assets/videos/brano-research-development-video.jpg?webp&w=1920&imagetools";
	import researchHeaderMobile from "../assets/videos/brano-research-development-header-mobile.mp4";
	import PartnersMap from "../components/PartnersMap.svelte";
	import carFooterBanner from "../assets/images/car-footer-banner.png?webp&w=1920&imagetools";
	import CanBeInterested from "../components/CanBeInterested.svelte";
	import TechnologySlider from "../components/TechnologySlider.svelte";
	import { getContext } from "svelte";
	import type { ProductGroupWithTextifiedContent } from "../../../core/schema/ProductGroup.js";
	import { productGroupsContextKey } from "../contexts/productGroupsContextKey.js";
	import { ProductPhotoPosition } from "../../../core/schema/ProductPhotoPosition.js";
	import { flyInOnScroll } from "../utils/flyInOnScroll";
	import ProductGroups from "../components/ProductGroups.svelte";
	import StaticProductBanner from "../components/StaticProductBanner.svelte";
	import { getTenant } from "../contexts/tenantContextKey";
	import { Locale, type Localized } from "../../../core/schema/Locale";
	import { nbspify } from "../../../core/utils/nbspify.js";

	const translate = getTranslate(translations);

	type $$Props = RnDProps;

	const { caseStudies, news } = $$restProps as $$Props;
	const productGroups = getContext<ProductGroupWithTextifiedContent[]>(productGroupsContextKey);

	const { locale } = getTenant();

	const bgImage: Localized<string> = {
		[Locale.cs]: RNDImageLg,
		[Locale.en]: RNDImageLgEn,
	};
</script>

<Layout
	menuTextColor="text-secondary"
	largeFooterImage
	footerImage={carFooterBanner}
	footerImageClass="max-lg:object-[80%_center]"
>
	<HeroBanner
		class="lg:gap-[10.125rem]"
		title={translate("heading")}
		backgroundVideo={researchHeader}
		backgroundVideoMobile={researchHeaderMobile}
		videoPoster={researchPoster}
		overlay="black"
		linkText={translate("showMore")}
		subheadline="{translate('subheading1')}<br />
			{translate('subheading2')}<br />
			{translate('subheading3')}<br />
			{translate('subheading4')}<br />"
	></HeroBanner>

	<section
		class="container flex flex-col justify-between gap-[3.0625rem] pb-20 pt-[8.875rem] lg:flex-row lg:pb-[8.75rem] lg:pt-[12.875rem] xl:gap-[13.25rem]"
		id="showMore"
	>
		<div class="flex max-w-[65.68rem] flex-col items-start gap-[3.0625rem] lg:gap-[6.0625rem]">
			<h2 use:flyInOnScroll class="m-0 p-0 text-xl font-semibold md:text-4xl md:leading-[1.135]">
				{translate("headingSection")}
			</h2>

			<p use:flyInOnScroll class="lg:text-2lg text-lg leading-[1.1875]">
				{translate("subheadingSection")}
			</p>
		</div>

		<div
			class="bg-tertiary flex flex-col gap-[1.5rem] pb-[2.78rem] pl-[1.7rem] pr-[2.68rem] pt-[3.05rem] lg:mr-8 lg:w-1/2 lg:min-w-[28rem] lg:gap-[2.5rem] lg:pb-[4.44rem] lg:pl-[4.5rem] lg:pr-[9rem] lg:pt-[4.87rem]"
		>
			<p
				use:flyInOnScroll
				class="m-0 p-0 text-[1.125rem] font-light leading-[1.44] lg:text-[1.4375rem] lg:leading-[1.52rem]"
			>
				{nbspify("Build-to-Print")}
			</p>
			<p
				use:flyInOnScroll
				class="m-0 p-0 text-[1.125rem] font-light leading-[1.44] lg:text-[1.4375rem] lg:leading-[1.52rem]"
			>
				{nbspify("Make-to-Spec")}
			</p>
			<p
				use:flyInOnScroll
				class="m-0 p-0 text-[1.125rem] font-light leading-[1.44] lg:text-[1.4375rem] lg:leading-[1.52rem]"
			>
				{translate("point1")}
			</p>
			<p
				use:flyInOnScroll
				class="m-0 p-0 text-[1.125rem] font-light leading-[1.44] lg:text-[1.4375rem] lg:leading-[1.52rem]"
			>
				{nbspify("Prototyping")}
			</p>
			<p
				use:flyInOnScroll
				class="m-0 p-0 text-[1.125rem] font-light leading-[1.44] lg:text-[1.4375rem] lg:leading-[1.52rem]"
			>
				{translate("point2")}
			</p>
		</div>
	</section>
	<section
		class="relative flex h-[51.375rem] max-h-screen justify-center overflow-hidden lg:block lg:max-h-none lg:min-h-screen"
	>
		<StaticPicture
			image={bgImage[locale]}
			alt={translate("idea")}
			width={1920}
			height={1032}
			imgClass="object-contain w-full"
			class="hidden h-full w-full md:block"
		/>

		<StaticPicture
			image={RNDImage}
			alt={translate("idea")}
			width={0}
			height={900}
			imgClass="object-cover h-full w-full"
			class="h-full w-full md:hidden"
		/>

		<div class="absolute inset-0 bg-black opacity-40 md:hidden"></div>

		<div
			class="container absolute top-0 z-20 flex h-full w-full flex-col items-start justify-between pb-[2.5rem] pt-[4rem] sm:pb-[2.1875rem] sm:pt-[3rem] md:hidden md:pb-20"
		>
			<h3
				class="text-secondary m-0 ml-3 w-1/2 p-0 text-[1.8rem] font-semibold leading-[1.13] sm:ml-0 sm:text-[2.75rem] md:w-1/4 md:text-[4.625rem]"
			>
				{translate("idea")}
			</h3>
			<StaticPicture
				image={getTenant().locale === Locale.cs ? RNDImageSm : RNDImageSmEn}
				alt={translate("idea")}
				width={331}
				height={430}
				imgClass="object-contain h-[26.2rem] w-[20.6875rem]"
				class="z-50 h-[26.2rem] w-[20.6875rem] "
			/>
		</div>
		<span class="absolute left-7"></span>
	</section>
	<section class="bg-tertiary">
		<div class="min-[2000px]:container">
			<StaticProductBanner
				photoPosition={ProductPhotoPosition.Right}
				name="Fullservice Design"
				content="{translate('RND1')} <br><br> {translate('RND1-1')}"
				image={RND1}
			/>
			<StaticProductBanner
				photoPosition={ProductPhotoPosition.Left}
				name={translate("simulations")}
				content={translate("RND2")}
				image={RND2}
			/>
			<StaticProductBanner
				photoPosition={ProductPhotoPosition.Right}
				name={translate("testingAndPrototyping")}
				content="{translate('RND3')} <br><br> {translate('RND3-1')}"
				image={RND3}
			/>
		</div>
	</section>
	<section class="bg-tertiary py-20">
		<CaseStudies class="bg-tertiary" {caseStudies} />
	</section>
	<section class="bg-secondary pt-20">
		<TechnologySlider class="bg-secondary" />
	</section>
	<section class="pb-[6rem] pt-[3.5rem] lg:pb-[20rem] lg:pt-[8rem]">
		<PartnersMap />
	</section>
	<section>
		<CanBeInterested {news} />
	</section>
	<section class="mt-[5rem] lg:mt-[15rem]">
		<div class="border-tertiary-300 container border-b py-8">
			<span class="text-[2rem] font-normal leading-[1.18] lg:text-[2.375rem]">{translate("whereElse")}</span>
			<h3
				use:flyInOnScroll
				class="text-lg font-semibold leading-[3.125rem] sm:text-[2.75rem] lg:text-[4.625rem] lg:leading-[5.25rem]"
			>
				{translate("exploreOurPortfolio")}
			</h3>
		</div>
		<div class="container">
			<ProductGroups {productGroups} topOffset={false} />
		</div>
	</section>
</Layout>
